/**
 * One Review-queue item per bill when a file holds several — TRBUD-318 item B
 * (Receipt parser follow-ups after go-live). Plan:
 * docs/plans/trbud-318-receipt-parser-followups.md, section 3, item B.
 *
 * ## The words
 *
 * - **File**: what somebody uploaded. Its own document is **bill 1**.
 * - **Bill**: a further bill in that file. It is a second `document` on the
 *   same stored bytes (`document.split_from_document_id`), with its own
 *   attachments and its own queue item, so it is logged, posted, exported and
 *   reported exactly as any file is.
 *
 * Production, 13 October 2026: of 42 files the reader had read, one held two
 * bills, it had been uploaded once, and one of its bills was never logged.
 *
 * ## Two ways a bill appears
 *
 * - **After a read**: every final bill the AI found other than the one it
 *   proposed becomes a bill with a COPY of that read (no second Foundry call).
 * - **By hand**: "Another bill in this file" on the Review item, which works
 *   with reading off (the cold-start path).
 *
 * Browser-safe: types, one zod schema and pure functions. Imports FROM
 * `requests.ts` and `receipt-continuation.ts`, never the other way (the module
 * load order note in `receipt-continuation.ts`).
 */
import { z } from "zod";
import { normaliseInvoiceNumber } from "./receipt-continuation.js";
import { idempotencyKey, strictObject } from "./requests.js";

/** The most bills one file may hold, its own document included (`document_split_bill_position_chk`). */
export const MAX_BILLS_PER_FILE = 20;

/** The longest key `document_split_bill_key_chk` admits. */
const BILL_KEY_MAX = 100;

/* ── keys ───────────────────────────────────────────────────────────── */

/**
 * The key a bill is found by on its file: its invoice number as two papers are
 * compared on it (`normaliseInvoiceNumber`), or `#<n>` when it has none, where
 * `n` is its place among the file's final bills. A number never contains `#`
 * (the normaliser drops it), so the two kinds never collide.
 *
 * The same read always gives the same keys, which is what makes a second
 * automatic split of one file create nothing new.
 */
export function receiptBillKey(invoiceNumber: unknown, ordinal: number): string {
  const number = typeof invoiceNumber === "string" ? normaliseInvoiceNumber(invoiceNumber) : null;
  return number !== null ? number.slice(0, BILL_KEY_MAX) : `#${ordinal}`;
}

/**
 * The key of a bill a person adds by hand at `position`: `#<position>`, which
 * an automatic split of an unnumbered bill at the same place finds again, or
 * `#added-<position>` when an earlier automatic split already took that key.
 * Never an existing key: adding by hand always makes a new bill.
 */
export function manualBillKey(position: number, taken: ReadonlySet<string>): string {
  const plain = `#${position}`;
  return taken.has(plain) ? `#added-${position}` : plain;
}

/** One document of a stored AI read, as far as splitting asks. */
export interface SplittableDocument {
  isFinalBill?: unknown;
  invoiceNumber?: unknown;
}

/**
 * The bills a read splits into their own items: every FINAL bill other than
 * the proposed one, with its key, in file order.
 *
 * - Nothing when the proposed document is not itself a final bill (the read
 *   found no bill at all) or there is no proposal.
 * - A bill whose number is the proposed bill's number is NOT split: two final
 *   bills with one number are almost always the customer and merchant copies
 *   of one bill, and `vendor_invoice` would refuse the second anyway.
 * - Two further bills with one key make one bill.
 * - At most `MAX_BILLS_PER_FILE - 1`, the positions 2 to 20.
 */
export function billsToSplit(
  documents: readonly SplittableDocument[],
  proposedIndex: number | null,
): Array<{ documentIndex: number; key: string }> {
  if (proposedIndex === null) return [];
  const proposed = documents[proposedIndex];
  if (!proposed || proposed.isFinalBill !== true) return [];
  const seen = new Set<string>();
  const proposedNumber =
    typeof proposed.invoiceNumber === "string" ? normaliseInvoiceNumber(proposed.invoiceNumber) : null;
  if (proposedNumber !== null) seen.add(proposedNumber.slice(0, BILL_KEY_MAX));
  const out: Array<{ documentIndex: number; key: string }> = [];
  let ordinal = 0;
  for (const [index, doc] of documents.entries()) {
    if (doc.isFinalBill !== true) continue;
    ordinal += 1;
    if (index === proposedIndex) continue;
    const key = receiptBillKey(doc.invoiceNumber, ordinal);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ documentIndex: index, key });
  }
  return out.slice(0, MAX_BILLS_PER_FILE - 1);
}

/* ── the one rule for adding a bill ─────────────────────────────────── */

/** What decides whether a bill can be added to a file. */
export interface ReceiptBillAddState {
  docClass: string;
  /** This document is itself a bill split from another file. */
  isSplitBill: boolean;
  /** The file is a page of another receipt, marked or joined (TRBUD-352). */
  isReceiptPage: boolean;
  /** Other pages are joined to this file as one receipt (TRBUD-352). */
  hasReceiptPages: boolean;
  /** The MaintainX import landed this file. */
  importedFromMainx: boolean;
  /** `document.scan_status`. */
  scanStatus: string;
  /** Still attached to a work order, purchase order, asset or request. */
  hasLiveParent: boolean;
  /** Bills in the file now, its own document included. */
  billCount: number;
}

/**
 * Why a bill cannot be added to this file, in the words the person reads, or
 * null when it can. The route and the Review screen both ask this, so the
 * button is never offered where the route would refuse.
 */
export function receiptBillAddRefusal(state: ReceiptBillAddState): string | null {
  if (state.docClass !== "invoice" && state.docClass !== "receipt") {
    return "Only an invoice or a receipt holds bills.";
  }
  if (state.isSplitBill) {
    return "This bill was split from another file. Add the next bill from the first bill in that file.";
  }
  if (state.isReceiptPage) {
    return "This file is a page of a receipt that runs over several files, so it holds one bill.";
  }
  if (state.hasReceiptPages) {
    return "Other pages are joined to this file as one receipt, so it holds one bill.";
  }
  if (state.importedFromMainx) {
    return "A file the MaintainX import landed cannot be split into bills.";
  }
  if (state.scanStatus === "failed" || state.scanStatus === "quarantined") {
    return "This file failed its malware scan, so it cannot be split into bills.";
  }
  if (!state.hasLiveParent) {
    return "This file is not attached to a work order, purchase order, asset or request any more.";
  }
  if (state.billCount >= MAX_BILLS_PER_FILE) {
    return `A file can hold at most ${MAX_BILLS_PER_FILE} bills.`;
  }
  return null;
}

/** "Bill 2 of 3". */
export function receiptBillLabel(position: number, count: number): string {
  return `Bill ${position} of ${count}`;
}

/* ── wire shapes ────────────────────────────────────────────────────── */

/** One bill of a file, as the Review item lists them. */
export interface ReceiptBillEntry {
  /** 1 for the file's own document. */
  position: number;
  /** That bill's queue item (its manual extraction), which Review opens. */
  itemId: string | null;
  documentId: string;
  /** "Invoice 1234" once logged or read, else "Bill 2". */
  label: string;
  /** Logged, or marked as nothing to log. */
  entered: boolean;
}

/** The bills of the file an item's document belongs to. */
export interface ReceiptBills {
  /** This item's bill: 1 for the file's own document. */
  position: number;
  /** Bills in the file, this one included. 1 until a bill is added. */
  count: number;
  /** The file's name, else its document id. */
  fileLabel: string;
  /** Every bill of the file, in position order. */
  entries: ReceiptBillEntry[];
  /**
   * Why "Another bill in this file" is not offered here
   * (`receiptBillAddRefusal`), or null when it is.
   */
  addRefusal: string | null;
}

/** A file's bill number and count, for the document lists' "Bill 2 of 3" tag. Null on a one-bill file. */
export interface ReceiptBillTag {
  position: number;
  count: number;
}

/** What "Another bill in this file" answers with. */
export interface ReceiptBillReceipt {
  /** The new bill's queue item, which Review opens next. */
  itemId: string;
  documentId: string;
  position: number;
  /** Bills in the file now. */
  count: number;
}

/** `POST /review/:itemId/bills`: add a bill to this item's file. */
export const addReceiptBillBody = strictObject({ idempotencyKey });
export type AddReceiptBillBody = z.infer<typeof addReceiptBillBody>;
